import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, CheckCircle2, User } from "lucide-react";

const VIEWS = [
  { key: "day", label: "Día" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
  { key: "year", label: "Año" },
];

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const startOfWeek = (d) => {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day);
  return x;
};
const startOfMonth = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), 1); return x; };
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtDate = (d) => d.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" });
const fmtTime = (d) => d.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });

const SlotChip = ({ slot, onClick }) => {
  const isBooked = slot.status === "booked";
  const d = new Date(slot.start_at);
  return (
    <button
      type="button"
      onClick={() => onClick?.(slot)}
      className={`w-full text-left rounded-md px-2 py-1 text-xs border transition-colors ${
        isBooked
          ? "bg-teal-deep text-white border-teal-deep hover:bg-teal"
          : "bg-orange/10 text-teal-deep border-orange/30 hover:bg-orange/20"
      }`}
      data-testid={`cal-slot-${slot.id}`}
    >
      <div className="font-bold inline-flex items-center gap-1">
        {isBooked ? <CheckCircle2 size={10} /> : <Clock size={10} />}
        {fmtTime(d)}
      </div>
      <div className="text-[10px] truncate opacity-90">
        {isBooked ? (slot.booked_by_name || "Reservado") : `${slot.duration_min}min`}
      </div>
    </button>
  );
};

// -------- DAY VIEW --------
const DayView = ({ cursor, slotsByDate, onSlotClick }) => {
  const dayKey = cursor.toISOString().slice(0, 10);
  const daySlots = (slotsByDate[dayKey] || []).slice().sort((a, b) => (a.start_at < b.start_at ? -1 : 1));
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="cal-day">
      <div className="text-xs font-bold uppercase tracking-wider text-teal-soft mb-3 capitalize">{fmtDate(cursor)}</div>
      {daySlots.length === 0 ? (
        <p className="text-teal-soft text-sm text-center py-8">Sin horarios este día.</p>
      ) : (
        <ul className="space-y-2">
          {daySlots.map((s) => {
            const isBooked = s.status === "booked";
            return (
              <li key={s.id} className="bg-cream/60 rounded-xl p-3 flex items-center gap-3" data-testid={`cal-day-slot-${s.id}`}>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isBooked ? "bg-teal text-white" : "bg-orange/15 text-orange"}`}>
                  {isBooked ? <CheckCircle2 size={16} /> : <Clock size={16} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-teal-deep">{fmtTime(new Date(s.start_at))} · {s.duration_min}min</div>
                  <div className="text-xs text-teal-soft">
                    {isBooked ? (
                      <span className="inline-flex items-center gap-1"><User size={11} /> {s.booked_by_name}</span>
                    ) : (s.topic_hint || "Disponible")}
                  </div>
                </div>
                <button type="button" onClick={() => onSlotClick?.(s)} className="text-xs text-orange font-bold hover:underline">Ver</button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// -------- WEEK VIEW --------
const WeekView = ({ cursor, slotsByDate, onSlotClick }) => {
  const monday = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="cal-week">
      {days.map((d, i) => {
        const key = d.toISOString().slice(0, 10);
        const daySlots = (slotsByDate[key] || []).slice().sort((a, b) => (a.start_at < b.start_at ? -1 : 1));
        const isToday = sameDay(d, new Date());
        return (
          <div key={i} className={`bg-white rounded-xl border p-2 min-h-[180px] ${isToday ? "border-orange bg-orange/5" : "border-gray-200"}`} data-testid={`cal-week-day-${key}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-teal-soft">{WEEKDAYS[i]}</div>
            <div className={`font-display text-lg leading-tight ${isToday ? "text-orange" : "text-teal-deep"}`}>{d.getDate()}</div>
            <div className="mt-2 space-y-1">
              {daySlots.slice(0, 4).map((s) => <SlotChip key={s.id} slot={s} onClick={onSlotClick} />)}
              {daySlots.length > 4 && (
                <div className="text-[10px] text-teal-soft text-center">+{daySlots.length - 4}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -------- MONTH VIEW --------
const MonthView = ({ cursor, slotsByDate, onDayClick }) => {
  const first = startOfMonth(cursor);
  const firstWeekday = (first.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  const today = new Date();
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="cal-month">
      <div className="grid grid-cols-7 bg-cream border-b border-gray-200">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-teal text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const cellKey = d ? `d-${d.toISOString().slice(0, 10)}` : `pad-${i}`;
          if (!d) return <div key={cellKey} className="min-h-[86px] border-r border-b border-gray-100 bg-gray-50/50" />;
          const key = d.toISOString().slice(0, 10);
          const daySlots = slotsByDate[key] || [];
          const isToday = sameDay(d, today);
          const openCount = daySlots.filter((s) => s.status !== "booked").length;
          const bookedCount = daySlots.filter((s) => s.status === "booked").length;
          return (
            <button
              key={cellKey}
              type="button"
              onClick={() => onDayClick?.(d)}
              className={`min-h-[86px] border-r border-b border-gray-100 p-1.5 text-left hover:bg-cream/60 transition-colors ${isToday ? "bg-orange/5" : ""}`}
              data-testid={`cal-month-day-${key}`}
            >
              <div className={`text-sm font-bold ${isToday ? "text-orange" : "text-teal-deep"}`}>{d.getDate()}</div>
              <div className="mt-1 space-y-0.5">
                {openCount > 0 && (
                  <div className="text-[10px] bg-orange/15 text-orange font-bold rounded px-1.5 py-0.5 inline-block">
                    {openCount} libre{openCount === 1 ? "" : "s"}
                  </div>
                )}
                {bookedCount > 0 && (
                  <div className="text-[10px] bg-teal text-white font-bold rounded px-1.5 py-0.5 inline-block">
                    {bookedCount} reserv.
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// -------- YEAR VIEW --------
const YearView = ({ cursor, slotsByDate, onMonthClick }) => {
  const year = cursor.getFullYear();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cal-year">
      {Array.from({ length: 12 }, (_, m) => {
        const first = new Date(year, m, 1);
        const days = new Date(year, m + 1, 0).getDate();
        let openCount = 0;
        let bookedCount = 0;
        for (let d = 1; d <= days; d++) {
          const key = new Date(year, m, d).toISOString().slice(0, 10);
          (slotsByDate[key] || []).forEach((s) => {
            if (s.status === "booked") bookedCount++; else openCount++;
          });
        }
        return (
          <button
            key={m}
            type="button"
            onClick={() => onMonthClick?.(first)}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-orange transition-colors"
            data-testid={`cal-year-month-${m}`}
          >
            <div className="text-xs uppercase tracking-wider font-bold text-teal-soft">{MONTH_NAMES[m]}</div>
            <div className="mt-2 flex gap-2 flex-wrap">
              {openCount > 0 && (
                <span className="text-[11px] bg-orange/15 text-orange font-bold rounded-full px-2 py-0.5">
                  {openCount} libre{openCount === 1 ? "" : "s"}
                </span>
              )}
              {bookedCount > 0 && (
                <span className="text-[11px] bg-teal text-white font-bold rounded-full px-2 py-0.5">
                  {bookedCount} reserv.
                </span>
              )}
              {openCount === 0 && bookedCount === 0 && (
                <span className="text-[11px] text-teal-soft">Sin horarios</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// -------- MAIN --------
const AgendaCalendar = ({ items = [], onSlotClick }) => {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));

  const slotsByDate = useMemo(() => {
    const map = {};
    items.forEach((s) => {
      const key = new Date(s.start_at).toISOString().slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [items]);

  const shift = (dir) => {
    const next = new Date(cursor);
    if (view === "day") next.setDate(next.getDate() + dir);
    else if (view === "week") next.setDate(next.getDate() + dir * 7);
    else if (view === "month") next.setMonth(next.getMonth() + dir);
    else if (view === "year") next.setFullYear(next.getFullYear() + dir);
    setCursor(next);
  };

  const label = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (view === "week") {
      const mon = startOfWeek(cursor);
      const sun = addDays(mon, 6);
      return `${mon.getDate()} ${MONTH_NAMES[mon.getMonth()].slice(0, 3)} – ${sun.getDate()} ${MONTH_NAMES[sun.getMonth()].slice(0, 3)} ${sun.getFullYear()}`;
    }
    if (view === "month") return `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
    return String(cursor.getFullYear());
  }, [view, cursor]);

  return (
    <div className="mt-6" data-testid="agenda-calendar">
      {/* Header: view switcher + nav */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-4 flex-wrap">
        <div className="inline-flex bg-cream rounded-xl p-1 gap-1" data-testid="cal-view-switcher">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={`text-xs font-bold uppercase tracking-wider rounded-lg px-3 py-2 transition-colors ${
                view === v.key ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"
              }`}
              data-testid={`cal-view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-1">
          <button
            type="button"
            onClick={() => shift(-1)}
            className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-cream"
            data-testid="cal-prev"
          >
            <ChevronLeft size={16} className="text-teal-deep" />
          </button>
          <button
            type="button"
            onClick={() => setCursor(startOfDay(new Date()))}
            className="text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 bg-white border border-gray-200 text-teal-deep hover:bg-cream"
            data-testid="cal-today"
          >
            Hoy
          </button>
          <button
            type="button"
            onClick={() => shift(1)}
            className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-cream"
            data-testid="cal-next"
          >
            <ChevronRight size={16} className="text-teal-deep" />
          </button>
          <span className="text-teal-deep font-display text-lg capitalize truncate" data-testid="cal-label">{label}</span>
        </div>
      </div>

      {/* Body */}
      {view === "day" && <DayView cursor={cursor} slotsByDate={slotsByDate} onSlotClick={onSlotClick} />}
      {view === "week" && <WeekView cursor={cursor} slotsByDate={slotsByDate} onSlotClick={onSlotClick} />}
      {view === "month" && <MonthView cursor={cursor} slotsByDate={slotsByDate} onDayClick={(d) => { setCursor(d); setView("day"); }} />}
      {view === "year" && <YearView cursor={cursor} slotsByDate={slotsByDate} onMonthClick={(d) => { setCursor(d); setView("month"); }} />}

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-xs text-teal-soft flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-orange/30 border border-orange" /> Libre
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-deep" /> Reservado
        </span>
      </div>
    </div>
  );
};

export default AgendaCalendar;
